import { JOINT_ACTIONS } from "./coverageVocabulary";
import type { Deferral } from "./coverage";

/* The (athlete, cell) deferral, read back on the coach's side.
 *
 * `docs/JOINT-ACTION-ENGINE.md` §15. The RULE has been finished since step 2 — `coverage.ts`
 * tests a deferral BEFORE the threshold, so a suspended cell reads `zeroDeclared` or, when the
 * week loads it anyway, `declaredButTrained` — and `goalGaps.ts` turns one a goal requires into
 * `blockedByDeferral`, the most important row this report can produce. What did not exist was
 * anywhere for an athlete to say one, which is why `coverage.ts` shipped
 * `DEFERRALS_HAVE_NO_HOME` and both screens printed it.
 *
 * It has one now: `profiles.deferred_actions`, written by the phone and read here through the
 * SAME row policy that already lets a coach read `injuries_data` — no policy was added.
 *
 * ⚠️ THE RULES ARE HAND-WRITTEN HERE AND EXECUTED BY `deferrals.test.ts`, matching this repo's
 * split: only the LIST is generated (`coverageVocabulary.ts`), because a list typed into two
 * systems is the defining bug class, while a rule typed twice is caught by two suites that both
 * have to pass. The Swift half is `ActionDeferral` and its gate drives the identical cases.
 *
 * 🔴 IT FILTERS NOTHING, HERE OR ON THE PHONE. A deferral marks a gap as intentional and says so
 * when the week trains that range anyway. Removing exercises because of one is his §8's step 5.
 */

/** One row of `profiles.deferred_actions`, exactly as stored. */
export type StoredDeferral = {
  readonly action: string;
  readonly reason: string;
  readonly reviewOn: string;
};

export type Declaration = {
  readonly action: string;
  readonly reason: string;
  readonly reviewOn: string;
};

/**
 * 🔴 A LAPSED DEFERRAL STAYS IN FORCE **AND** IS REPORTED.
 *
 * Both obvious readings throw something away. Keeping it forever makes the review date
 * decoration; dropping it on the day silently deletes the athlete's statement and flips the cell
 * back to `zeroAccidental` with nothing saying why — a deletion with better manners, automated.
 * So the DECLARATION carries the lapse and the CELL is untouched.
 */
export type DeferralStatus =
  | { readonly kind: "active" }
  | { readonly kind: "dueForReview"; readonly daysOverdue: number };

/** Three answers, and the third is never silent — `StoredGoal`'s shape, one layer out. */
export type DeferralResolution = {
  readonly declarations: readonly Declaration[];
  /** Stored actions no entry in the generated taxonomy matches. Reported, never dropped. */
  readonly unrecognised: readonly string[];
  /** Rows with a blank reason or a `reviewOn` that is not a real civil date. */
  readonly malformed: readonly string[];
};

const ACTION_IDS = new Set(JOINT_ACTIONS.map((a) => a.id));

export function resolve(stored: readonly StoredDeferral[]): DeferralResolution {
  const byAction = new Map<string, Declaration>();
  const order: string[] = [];
  const unrecognised: string[] = [];
  const malformed: string[] = [];

  for (const row of stored) {
    if (!ACTION_IDS.has(row.action)) {
      unrecognised.push(row.action);
      continue;
    }
    const reason = (row.reason ?? "").trim();
    if (reason === "" || !isISODay(row.reviewOn)) {
      malformed.push(row.action);
      continue;
    }
    const candidate: Declaration = { action: row.action, reason, reviewOn: row.reviewOn };
    // A duplicate keeps the LATER review date, so it can only ever lengthen a suspension and
    // never shorten one. ISO days compare lexicographically, which is chronologically.
    const existing = byAction.get(row.action);
    if (existing === undefined) {
      byAction.set(row.action, candidate);
      order.push(row.action);
    } else if (candidate.reviewOn > existing.reviewOn) {
      byAction.set(row.action, candidate);
    }
  }
  return {
    declarations: order.map((a) => byAction.get(a)!),
    unrecognised,
    malformed,
  };
}

/**
 * Every cell a declaration suspends — the action crossed with the zones IT carries.
 *
 * Read from the generated `JOINT_ACTIONS[].zones`, never from anything the row stores, which is
 * why an isometric `anti…` action yields one cell and everything else three. Storing a zone list
 * would freeze today's answer into every old row.
 */
export function cells(d: Declaration): Deferral[] {
  const info = JOINT_ACTIONS.find((a) => a.id === d.action);
  if (info === undefined) return [];
  return info.zones.map((zone) => ({
    action: d.action, zone, reason: d.reason, reviewOn: d.reviewOn,
  }));
}

/** Every cell of every declaration — what `MatrixInput.deferrals` wants. */
export function deferredCells(r: DeferralResolution): Deferral[] {
  return r.declarations.flatMap(cells);
}

/** `today` is an ISO day the CALLER supplies, so no timezone is decided in here. */
export function status(d: Declaration, today: string): DeferralStatus {
  if (d.reviewOn >= today) return { kind: "active" };
  return { kind: "dueForReview", daysOverdue: daysBetween(d.reviewOn, today) };
}

/** `YYYY-MM-DD`, validated as a real civil date: `2026-02-31` matches every plausible regex. */
export function isISODay(s: string): boolean {
  return civilDays(s) !== null;
}

export function daysBetween(from: string, to: string): number {
  const a = civilDays(from);
  const b = civilDays(to);
  if (a === null || b === null) return 0;
  return b - a;
}

/**
 * Days since 1970-01-01 for a proleptic Gregorian civil date — Howard Hinnant's
 * `days_from_civil`. Deliberately NOT `new Date(s)`: that parses a bare `YYYY-MM-DD` as UTC and
 * a `YYYY-MM-DDTHH:MM` as local, so a review date would drift by a day depending on the
 * viewer's timezone. A review date is a DAY the athlete chose.
 */
function civilDays(s: string): number | null {
  if (typeof s !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m === null) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1) return null;
  const leap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
  const lengths = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const len = lengths[mo - 1];
  if (len === undefined || d > len) return null;
  const yy = mo <= 2 ? y - 1 : y;
  const era = Math.floor((yy >= 0 ? yy : yy - 399) / 400);
  const yoe = yy - era * 400;
  const doy = Math.floor((153 * (mo + (mo > 2 ? -3 : 9)) + 2) / 5) + d - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146097 + doe - 719468;
}

/**
 * What a row the athlete stored but nothing can read should say to the coach.
 *
 * Empty when there is nothing to report, so a screen can append it unconditionally — the
 * `describeShape`/`considered` precedent: an empty summary and "everything is fine" must not be
 * the same value for two different reasons.
 */
export function describeUnreadable(r: DeferralResolution): string {
  const parts: string[] = [];
  if (r.unrecognised.length > 0) {
    parts.push(`${r.unrecognised.length} this build does not recognise`);
  }
  if (r.malformed.length > 0) {
    parts.push(`${r.malformed.length} missing a reason or a review date`);
  }
  if (parts.length === 0) return "";
  return `Also stored, and not shown: ${parts.join(", ")}.`;
}

/* ── What the two coach screens SAY about the declarations ────────────────────────────────
 *
 * ⚠️ THESE LIVE HERE RATHER THAN INLINE IN THE TSX BECAUSE A STRING BUILT IN A SCREEN IS THE ONE
 * FALLIBLE THING WITH NO RUNNER. The first cut of both was a template literal in the component,
 * and it shipped a real defect nothing could have caught: the sentence began with a lowercase
 * constant, so the rendered paragraph read *"…a gap in the library rather than in this athlete.
 * until 2026-08-31 a declared zero…"*. Visible in a screenshot, invisible in review.
 *
 * TWO FUNCTIONS AND NOT ONE, because they are two sentences for two questions — the Coverage tab
 * asks what this week trains, the Goals tab asks what the athlete's goals require — and folding
 * them into one parameterised sentence would make one screen answer the other's question.
 *
 * 🔴 AND NEITHER RENDERS THE PROJECT'S OWN HISTORY. Both once printed
 * *"a declared zero needs an (athlete, cell) table; none exists"*, which was honest while it was
 * true and is a developer's note either way: a coach reading this screen needs to know HOW TO
 * READ THE ZEROS below it, not when the column landed. The empty case says that instead.
 */

function overdueCount(r: DeferralResolution, today: string): number {
  return r.declarations.filter((d) => status(d, today).kind === "dueForReview").length;
}

/** ⚠️ Still in force, and said so — see `DeferralStatus`. */
function overdueClause(r: DeferralResolution, today: string): string {
  const n = overdueCount(r, today);
  if (n === 0) return "";
  return ` ${n} of them passed the review date the athlete set and still stand.`;
}

function tail(r: DeferralResolution, today: string): string {
  const u = describeUnreadable(r);
  return overdueClause(r, today) + (u === "" ? "" : ` ${u}`);
}

/** The Coverage tab: how to read the empty cells in the table below. */
export function coverageDeferralNote(r: DeferralResolution, today: string): string {
  if (r.declarations.length === 0) {
    return "This athlete has declared no movements suspended, so every empty range below is an"
      + " accidental gap rather than a deliberate one."
      + (describeUnreadable(r) === "" ? "" : ` ${describeUnreadable(r)}`);
  }
  const n = r.declarations.length;
  return `${n} movement${n === 1 ? " is" : "s are"} declared suspended by the athlete and`
    + " marked Deferred below, whatever the week trains."
    + tail(r, today);
}

/** The Goals tab: what a declaration does to a REQUIRED range. */
export function goalsDeferralNote(r: DeferralResolution, today: string): string {
  if (r.declarations.length === 0) {
    return "No movements are declared suspended, so nothing above is blocked by the athlete's"
      + " own decision."
      + (describeUnreadable(r) === "" ? "" : ` ${describeUnreadable(r)}`);
  }
  const n = r.declarations.length;
  return `${n} movement${n === 1 ? " is" : "s are"} declared suspended by the athlete; a goal`
    + " that requires one of those ranges is reported as blocked rather than as a gap."
    + tail(r, today);
}
